import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useAccounts } from "./useAccounts";

import logger from "../utils/secureLogger";
/**
 * Hook para datos del dashboard multi-cloud
 * Usa únicamente account-information-summary como fuente de datos
 */
export const useMultiCloudDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isInitialized = useRef(false);

  // Obtener datos de account-information-summary (única fuente de datos)
  const { 
    accounts: generalAccounts, 
    loading: generalLoading,
    error: generalError,
    loadAccounts 
  } = useAccounts();

  // Función estable para cargar datos
  const loadDashboardData = useCallback(async () => {
    if (isInitialized.current) return;
    
    try {
      setLoading(true);
      setError(null);
      isInitialized.current = true;
      
      await loadAccounts();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [loadAccounts]);

  // Función para refrescar datos manualmente
  const refreshData = useCallback(async () => {
    isInitialized.current = false;
    await loadDashboardData();
  }, [loadDashboardData]);

  // Cargar datos solo una vez al montar
  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Función para determinar si una cuenta está activa (MISMA LÓGICA QUE /accounts)
  const isAccountActive = (account) => {
    // Usar la misma lógica que el filtro Status en /accounts
    return !account.Decommissioned;
  };

  // Función para calcular budget de cuentas activas
  const calculateActiveBudget = (accounts) => {
    return accounts
      .filter(acc => isAccountActive(acc))
      .reduce((sum, acc) => {
        // Handle both decimal (new API) and string (legacy) budget formats
        const budget = typeof acc.Budget === 'number' 
          ? acc.Budget 
          : parseFloat(acc.Budget?.toString().replace(/[USD$,\s]/g, '') || '0');
        return sum + budget;
      }, 0);
  };

  // Procesar datos por proveedor de nube usando SOLO account-information-summary
  const cloudProviderStats = useMemo(() => {
    logger.debug('🔍 Processing Accounts from /functions/account-information-summary:', generalAccounts.length);
    logger.debug('🔍 Sample Account:', generalAccounts[0]);
    logger.debug('🔍 Available fields:', Object.keys(generalAccounts[0] || {}));
    
    // ANÁLISIS DETALLADO DE TODOS LOS PROVEEDORES
    const allProviders = generalAccounts.map(acc => ({
      CloudProvider: acc.CloudProvider,
      Type: acc.Type,
      Budget: acc.Budget,
      Decommissioned: acc.Decommissioned,
      AccountName: acc.AccountName
    }));
    
    logger.debug('🔍 ALL PROVIDERS ANALYSIS:', {
      totalAccounts: generalAccounts.length,
      uniqueCloudProviders: [...new Set(allProviders.map(acc => acc.CloudProvider))],
      sampleData: allProviders.slice(0, 10),
      decommissionedStats: {
        active: allProviders.filter(acc => !acc.Decommissioned).length,
        inactive: allProviders.filter(acc => acc.Decommissioned).length
      }
    });
    
    // FILTRADO EXACTO - Usar CloudProvider que sí tiene datos
    const awsLlaAccounts = generalAccounts.filter(acc => {
      const cloudProvider = (acc.CloudProvider || '').toLowerCase().trim();
      return cloudProvider === 'aws-lla';
    });
    
    const awsCrAccounts = generalAccounts.filter(acc => {
      const cloudProvider = (acc.CloudProvider || '').toLowerCase().trim();
      return cloudProvider === 'aws-cr';
    });
    
    // Debug específico para AWS CR
    logger.debug('🔍 AWS CR ACCOUNTS ANALYSIS:', {
      totalCount: awsCrAccounts.length,
      accountTypes: awsCrAccounts.map(acc => ({
        AccountName: acc.AccountName,
        Type: acc.Type,
        Budget: acc.Budget
      })),
      uniqueTypes: [...new Set(awsCrAccounts.map(acc => acc.Type))]
    });
    
    const gcpAccounts = generalAccounts.filter(acc => {
      const cloudProvider = (acc.CloudProvider || '').toLowerCase().trim();
      return cloudProvider === 'gcp-lla';
    });
    
    logger.debug('📊 FILTERED ACCOUNTS BY PROVIDER:', {
      'AWS LLA': {
        count: awsLlaAccounts.length,
        sampleAccounts: awsLlaAccounts.slice(0, 3).map(acc => ({
          CloudProvider: acc.CloudProvider,
          Type: acc.Type,
          Budget: acc.Budget
        }))
      },
      'AWS CR': {
        count: awsCrAccounts.length,
        sampleAccounts: awsCrAccounts.slice(0, 3).map(acc => ({
          CloudProvider: acc.CloudProvider,
          Type: acc.Type,
          Budget: acc.Budget
        }))
      },
      'GCP': {
        count: gcpAccounts.length,
        sampleAccounts: gcpAccounts.slice(0, 3).map(acc => ({
          CloudProvider: acc.CloudProvider,
          Type: acc.Type,
          Budget: acc.Budget
        }))
      }
    });

    // Función para procesar cada proveedor
    const processProvider = (accounts, providerName) => {
      const activeAccounts = accounts.filter(acc => isAccountActive(acc));
      const inactiveAccounts = accounts.filter(acc => !isAccountActive(acc));
      
      // Debug budget calculation
      const budgetDetails = activeAccounts.map(acc => {
        const budgetStr = acc.Budget?.toString() || '0';
        const budgetNum = typeof acc.Budget === 'number' 
          ? acc.Budget 
          : parseFloat(budgetStr.replace(/[USD$,\s]/g, '') || '0');
        return {
          AccountName: acc.AccountName,
          BudgetString: budgetStr,
          BudgetNumber: budgetNum,
          Type: acc.Type,
          Decommissioned: acc.Decommissioned
        };
      });
      
      const activeBudget = budgetDetails.reduce((sum, item) => sum + item.BudgetNumber, 0);

      logger.debug(`📊 ${providerName} DETAILED ANALYSIS:`, {
        totalAccounts: accounts.length,
        activeAccounts: activeAccounts.length,
        inactiveAccounts: inactiveAccounts.length,
        activeBudget: activeBudget,
        budgetDetails: budgetDetails.slice(0, 5), // Primeros 5 para debug
        decommissionedBreakdown: {
          active: accounts.filter(acc => !acc.Decommissioned).length,
          inactive: accounts.filter(acc => acc.Decommissioned).length
        },
        sampleDecommissionedStatus: accounts.slice(0, 5).map(acc => ({
          AccountName: acc.AccountName,
          Decommissioned: acc.Decommissioned,
          Type: acc.Type
        }))
      });

      return {
        activeCount: activeAccounts.length,
        inactiveCount: inactiveAccounts.length,
        totalCount: accounts.length,
        activeBudget: activeBudget
      };
    };

    // Procesar cada proveedor
    const awsLlaStats = processProvider(awsLlaAccounts, 'AWS LLA');
    const awsCrStats = processProvider(awsCrAccounts, 'AWS CR');
    const gcpStats = processProvider(gcpAccounts, 'GCP');

    const providers = [
      {
        id: 'aws-lla',
        name: 'AWS LLA',
        logo: '/aws-logo.png',
        color: 'bg-orange-500',
        accounts: awsLlaAccounts,
        activeAccounts: awsLlaStats.activeCount,
        inactiveAccounts: awsLlaStats.inactiveCount,
        totalAccounts: awsLlaStats.totalCount,
        totalBudget: awsLlaStats.activeBudget
      },
      {
        id: 'aws-cr',
        name: 'AWS CR',
        logo: '/aws-logo.png',
        color: 'bg-blue-500',
        accounts: awsCrAccounts,
        activeAccounts: awsCrStats.activeCount,
        inactiveAccounts: awsCrStats.inactiveCount,
        totalAccounts: awsCrStats.totalCount,
        totalBudget: awsCrStats.activeBudget
      },
      {
        id: 'gcp-lla',
        name: 'Google Cloud',
        logo: '/gcp-logo.png',
        color: 'bg-green-500',
        accounts: gcpAccounts,
        activeAccounts: gcpStats.activeCount,
        inactiveAccounts: gcpStats.inactiveCount,
        totalAccounts: gcpStats.totalCount,
        totalBudget: gcpStats.activeBudget
      },
      {
        id: 'azure',
        name: 'Microsoft Azure',
        logo: '/azure-logo.png',
        color: 'bg-blue-600',
        accounts: [],
        activeAccounts: 0,
        inactiveAccounts: 0,
        totalAccounts: 0,
        totalBudget: 0
      },
      {
        id: 'oci',
        name: 'Oracle Cloud',
        logo: '/oci-logo.png',
        color: 'bg-red-500',
        accounts: [],
        activeAccounts: 0,
        inactiveAccounts: 0,
        totalAccounts: 0,
        totalBudget: 0
      }
    ];

    logger.debug('📈 FINAL PROVIDER STATS:', providers.map(p => ({
      name: p.name,
      active: p.activeAccounts,
      inactive: p.inactiveAccounts,
      total: p.totalAccounts,
      budget: p.totalBudget
    })));

    return providers;
  }, [generalAccounts]);

  // Estadísticas generales
  const overallStats = useMemo(() => {
    const totalActive = cloudProviderStats.reduce((sum, provider) => sum + provider.activeAccounts, 0);
    const totalInactive = cloudProviderStats.reduce((sum, provider) => sum + provider.inactiveAccounts, 0);
    const totalBudget = cloudProviderStats.reduce((sum, provider) => sum + provider.totalBudget, 0);

    logger.debug('📈 OVERALL STATS FINAL:', {
      totalAccounts: totalActive + totalInactive,
      activeAccounts: totalActive,
      inactiveAccounts: totalInactive,
      monthlyBudget: totalBudget
    });

    return {
      totalAccounts: totalActive + totalInactive,
      activeAccounts: totalActive,
      inactiveAccounts: totalInactive,
      monthlyCost: totalBudget // Mantenemos el nombre para compatibilidad con OverallStatsCard
    };
  }, [cloudProviderStats]);

  return {
    cloudProviderStats,
    overallStats,
    loading: loading || generalLoading,
    error: error || generalError,
    refreshData
  };
};
